import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SendApprovalEmailParams {
  boletimId: string;
  emailDestinatario: string;
  origem?: 'automatico' | 'manual';
}

export function useSendApprovalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boletimId, emailDestinatario, origem = 'manual' }: SendApprovalEmailParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Não autenticado');

      // 1. Fetch boletim + contract details for the email
      const { data: boletim, error: boletimError } = await supabase
        .from('emp_boletins_medicao')
        .select(`
          id, numero_boletim, valor_liquido, periodo_inicio, periodo_fim,
          emp_contratos!inner(id, numero_contrato, nome)
        `)
        .eq('id', boletimId)
        .single();

      if (boletimError || !boletim) throw new Error('Boletim não encontrado');

      const contrato = (boletim as any).emp_contratos;

      // 2. Create approval token
      const { data: tokenRecord, error: tokenError } = await supabase
        .from('net_approval_tokens')
        .insert({
          boletim_id: boletimId,
          email_destinatario: emailDestinatario,
          origem,
          enviado_por: user.id,
        })
        .select('id, token, expires_at')
        .single();

      if (tokenError || !tokenRecord) throw new Error('Erro ao gerar token de aprovação');

      // 3. Build approval URLs
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const baseApprovalUrl = `${supabaseUrl}/functions/v1/handle-boletim-approval`;
      const approveUrl = `${baseApprovalUrl}?token=${tokenRecord.token}&action=aprovar`;
      const rejectUrl = `${baseApprovalUrl}?token=${tokenRecord.token}&action=reprovar`;

      // Format values
      const valorFormatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(boletim.valor_liquido) || 0);

      const formatDate = (d: string | null) => {
        if (!d) return '';
        return new Date(d).toLocaleDateString('pt-BR');
      };

      const expiresFormatted = new Date(tokenRecord.expires_at).toLocaleString('pt-BR');

      // 4. Send transactional email
      const idempotencyKey = `boletim-approval-${boletimId}-${tokenRecord.id}`;

      const { error: emailError } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'boletim-approval',
          recipientEmail: emailDestinatario,
          idempotencyKey,
          templateData: {
            contratoNumero: contrato.numero_contrato,
            contratoNome: contrato.nome,
            boletimNumero: boletim.numero_boletim,
            valorMedicao: valorFormatted,
            periodoInicio: formatDate(boletim.periodo_inicio),
            periodoFim: formatDate(boletim.periodo_fim),
            approveUrl,
            rejectUrl,
            expiresAt: expiresFormatted,
          },
        },
      });

      if (emailError) throw new Error('Erro ao enviar e-mail de aprovação');

      return { tokenId: tokenRecord.id, email: emailDestinatario };
    },
    onSuccess: (data, vars) => {
      toast.success(`E-mail de aprovação enviado para ${data.email}`);
      queryClient.invalidateQueries({ queryKey: ['net-approval-tokens', vars.boletimId] });
      queryClient.invalidateQueries({ queryKey: ['net-external-approvals', vars.boletimId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao enviar e-mail de aprovação');
    },
  });
}
